import { useRef, useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { FeatureGroup } from "react-leaflet";
import EditControlWrapper from "../../../../components/map/EditControlWrapper";
import MapDraw from "../../../../components/map/map.component";
import { ZoomIn, ZoomOut, PlusCircle, Edit3, Trash2 } from "lucide-react";
import {
  setActiveGeofence,
  saveGeofence,
  deleteGeofence
} from "../../../../redux/geofence/geofenceActions";
import SuccessAlert from "./SuccessAlert.component";
import ErrorAlert from "./ErrorAlert.component";
import L from "leaflet";
import { useMapRef } from "../../../../context/MapRefContext.jsx";
import { trackDevice } from "../../../../redux/assets/assetsActions";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

export const GeofenceView = (props) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const geofences = useSelector((state) => state.geofences.geofences);
  const activeGeofence = useSelector((state) => state.geofences.activeGeofence);
  const user = useSelector((state) => state.user);
  
  const devices = useSelector((state) => state.assets.devices);
  const locations = devices
    .filter(device => device.locations && device.locations.length > 0)
    .map(device => [device.locations[0].lat, device.locations[0].long]);

  const editRef = useRef();
  const mapContainerRef = useRef(null);
  const mapRef = useMapRef();
  const [map, setMap] = useState(null);
  const [editableLayer, setEditableLayer] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);

  const handleMapReady = (mapInstance) => setMap(mapInstance);

  useEffect(() => {
    if (map) {
      const timer = setTimeout(() => map.invalidateSize(), 100);
      return () => clearTimeout(timer);
    }
  }, [map]);

  useEffect(() => { dispatch(trackDevice(null)); }, [dispatch]);

  useEffect(() => {
    if (map && geofences) {
      if (editableLayer) editableLayer.clearLayers();
      const layerGroup = L.featureGroup().addTo(map);
      geofences?.forEach((geofence) => {
        const { geometry, properties } = geofence;
        let layerOptions = { color: geofence.color, geofenceId: geofence._id };
        let layer;
        if (geometry.type === "polygon") {
          const positions = geometry.coordinates[0].map(([lat, lng]) => [lng, lat]);
          layer = L.polygon(positions, layerOptions);
        } else if (geometry.type === "circle") {
          const latitude = geometry.coordinates[0][0][0];
          const longitude = geometry.coordinates[0][0][1];
          const radius = properties?.radius || 1000;
          layer = L.circle([longitude, latitude], { ...layerOptions, radius: Number(radius) });
        } else if (geometry.type === "rectangle") {
          const bounds = geometry.coordinates[0].map(([lat, lng]) => [lng, lat]);
          layer = L.rectangle(bounds, layerOptions);
        } else if (geometry.type === "circlemarker") {
          const latitude = geometry.coordinates[0][0][0];
          const longitude = geometry.coordinates[0][0][1];
          layer = L.circleMarker([longitude, latitude], layerOptions);
        } else if (geometry.type === "polyline") {
          const positions = geometry.coordinates[0].map(([lat, lng]) => [lng, lat]);
          layer = L.polyline(positions, layerOptions);
        }
        if (layer && properties?.name) {
          layer.bindTooltip(properties.name, { 
            permanent: false, direction: 'top', 
            offset: L.point(0, -layer.options.radius || -10)
          });
        }
        layer?.on("click", () => { dispatch(setActiveGeofence(geofence)); });
        layerGroup.addLayer(layer);
      });
      setEditableLayer(layerGroup);
    }
  }, [map, geofences, dispatch, activeGeofence]);

  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
    
    setTimeout(() => {
      if (map) {
        map.invalidateSize();
      }
    }, 100);
  };

  const onShapeDrawn = (e) => console.log("Shape drawn in GeofenceView, should happen on EditPage", e);
  const onShapeEdited = (e) => console.log("Shape edited in GeofenceView, should happen on EditPage", e);
  const onShapeDeletedFromMap = (e) => {
    console.log("Shape deleted from map controls in GeofenceView", e);
  };
  
  const handleNavigateToEdit = (geofenceId) => {
    navigate(`/dashboard/fleet/geofence/edit/${geofenceId}`);
  };

  const handleAddNewGeofence = () => {
    navigate("/dashboard/fleet/geofence/new");
  };

  const handleDeleteGeofence = (geofenceId) => {
    if (geofenceId && user?.currentUser?.user?._id && user?.currentUser?.token) {
      dispatch(deleteGeofence(user.currentUser.user._id, geofenceId, user.currentUser.token));
      if (activeGeofence?._id === geofenceId) {
          dispatch(setActiveGeofence(null));
      }
    }
  };

  return (
    <div className={cn("flex flex-col md:flex-row h-full gap-2 p-2")}>
      <div ref={mapContainerRef} className={cn("md:w-2/3 lg:w-3/4 relative h-[300px] md:h-full order-2 md:order-1")}>
        <div className={cn(
          "h-full w-full rounded shadow-md",
          isFullscreen ? "fixed inset-0 z-[100]" : "relative z-[1]"
        )}>
          <button onClick={toggleFullscreen} className="absolute top-2.5 right-12 z-[101] p-1.5 bg-white rounded shadow">
            {isFullscreen ? <ZoomOut size={20} /> : <ZoomIn size={20} />}
          </button>
          <MapDraw route={locations} onMapReady={handleMapReady} style={{ height: "100%", width: "100%" }}>
            <FeatureGroup ref={editRef}>
              <EditControlWrapper 
                position="topright" 
                onCreated={onShapeDrawn} 
                onEdited={onShapeEdited} 
                onDeleted={onShapeDeletedFromMap} 
                draw={{rectangle: false, circle: false, polygon: false, marker: false, circlemarker: false, polyline: false}}
                edit={{remove: false, edit: false}}
              />
            </FeatureGroup>
          </MapDraw>
        </div>
      </div>

      <div className="md:w-1/3 lg:w-1/4 h-full flex flex-col gap-2 order-1 md:order-2">
        <Card className="flex-shrink-0">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex justify-between items-center">
              Geofences
              <Button size="sm" onClick={handleAddNewGeofence}><PlusCircle size={16} className="mr-1.5"/> Add New</Button>
            </CardTitle>
          </CardHeader>
        </Card>
        
        <Card className="flex-grow overflow-hidden flex flex-col">
          <CardContent className="overflow-y-auto flex-grow p-2 space-y-1.5">
            {geofences && geofences.length > 0 ? geofences.map(gf => (
              <div 
                key={gf._id} 
                className={cn(
                    "p-2 rounded border flex justify-between items-center cursor-pointer hover:bg-gray-100",
                    activeGeofence?._id === gf._id && "bg-blue-100 border-blue-400"
                )}
                onClick={() => dispatch(setActiveGeofence(gf))}
              >
                <div className="flex items-center">
                  <div style={{ width: 12, height: 12, backgroundColor: gf.color || '#ccc', borderRadius: '50%', marginRight: 8 }}></div>
                  <span className="text-sm font-medium">{gf.name}</span>
                </div>
                <div>
                  <Button variant="ghost" size="icon" className="h-7 w-7" onClick={(e) => { e.stopPropagation(); handleNavigateToEdit(gf._id);}}><Edit3 size={14}/></Button>
                  <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500 hover:text-red-600" onClick={(e) => { e.stopPropagation(); handleDeleteGeofence(gf._id);}}><Trash2 size={14}/></Button>
                </div>
              </div>
            )) : <p className="text-sm text-gray-500 text-center py-4">No geofences found.</p>}
          </CardContent>
        </Card>
      </div>
      <SuccessAlert />
      <ErrorAlert />
    </div>
  );
};

export default GeofenceView; 